"use client";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import Image from "next/image";
import PatternCircles from "@/app/assets/images/announcement-bg-circles.png";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { FCM } from "@/app/interface/homepage";
import ThinCross from "@/app/assets/icons/thin-cross";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
// import { initBranch, trackEvent } from "@/app/utils/branchio";

interface AnnouncementDialogProps {
    renderAnnouncement: boolean;
    setrenderAnnouncement: (value: boolean) => void;
    data?: FCM;
}

export function AnnouncementDialog({
    renderAnnouncement,
    setrenderAnnouncement,
    data,
}: AnnouncementDialogProps) {
    const { cleverTapTrackEvent } = useCleverTapEvents();

    return (
        <Dialog open={renderAnnouncement}>
            <DialogContent className="w-[90%] overflow-hidden !rounded-2xl border-0 p-0 md:w-full md:max-w-[480px] [&>button:last-child]:hidden">
                {/* Accessible title/description for screen readers (Radix requires a
                    DialogTitle + description); the visible heading is styled separately below. */}
                <VisuallyHidden.Root>
                    <DialogTitle>{data?.title || "Announcement"}</DialogTitle>
                    <DialogDescription>{data?.body || "Announcement details"}</DialogDescription>
                </VisuallyHidden.Root>
                {/* Close Button positioned at top right inside content */}
                <div
                    onClick={() => {
                        setAnalyticsEvent("", "InApp_Popup_Cross");
                        cleverTapTrackEvent("InApp_Popup_Cross", {});
                        // initBranch()
                        //     .then(() => {
                        //         trackEvent("InApp_Popup_Cross");
                        //         setTimeout(() => {
                        //             setrenderAnnouncement(false);
                        //         }, 500);
                        //     })
                        //     .catch((error) => {
                        //         console.error("Error initializing Branch:", error);
                        //         setrenderAnnouncement(false);
                        //     });
                        setrenderAnnouncement(false);
                    }}
                    className="absolute right-3 top-3 z-10 flex items-center justify-center border-0"
                >
                    <ThinCross className="h-3 w-3 text-white md:h-4 md:w-4 cursor-pointer" />
                </div>

                <div className="flex flex-col">
                    {/* Header */}
                    <div
                        className="relative px-4 py-[14.21px] text-white md:px-6"
                        style={{
                            backgroundColor: data?.headerBackgroundColor || "#F26A21",
                        }}
                    >
                        <div className="relative md:mt-3">
                            {/* Pattern circles in top left of the orange header */}
                            {data?.headerBackgroundImage && (
                                <div className="absolute -left-6 -top-6">
                                    <Image
                                        src={`${data?.headerBackgroundImage || PatternCircles.src}`}
                                        alt="Pattern circles"
                                        width={150}
                                        height={100}
                                        className="max-h-[100px] max-w-[150px] object-cover opacity-70"
                                    />
                                </div>
                            )}

                            <div className="flex items-start justify-between">
                                {data?.topImage ? (
                                    <div className="relative w-full">
                                        <div className="h-[100px] w-full animate-pulse rounded bg-gray-200 md:h-[138px] md:max-w-[432px]" />
                                        <Image
                                            src={data?.topImage}
                                            alt="Announcement megaphone"
                                            width={100}
                                            height={100}
                                            className="absolute left-0 top-0 max-h-[100px] w-full object-cover md:max-h-[138px] md:min-h-[100px] md:max-w-[432px]"
                                            onLoad={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.opacity = "1";
                                            }}
                                            style={{ opacity: 0, transition: "opacity 0.3s" }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col items-center bg-white p-4 md:p-6 w-full">
                        {data?.title ? (
                            <h3 className="mb-3 text-center text-xl font-semibold leading-[166%] tracking-[-0.24px] md:text-3xl">
                                {data?.title}
                            </h3>
                        ) : (
                            <div className="mb-3 h-7 w-3/4 animate-pulse rounded bg-gray-200 md:h-9"></div>
                        )}
                        {data?.body ? (
                            <p className="text-center text-wrap text-base font-medium leading-[160%] text-neutral-500">
                                {data?.body}
                            </p>
                        ) : (
                            <div className="space-y-2">
                                <div className="h-5 w-full animate-pulse rounded bg-gray-200"></div>
                                <div className="mx-auto h-5 w-5/6 animate-pulse rounded bg-gray-200"></div>
                                <div className="mx-auto h-5 w-4/6 animate-pulse rounded bg-gray-200"></div>
                            </div>
                        )}
                        <div className="my-5 md:my-8">
                            <Image
                                src={data?.bottomImage ?? ""}
                                alt={data?.title ?? ""}
                                width={432}
                                height={200}
                                className={`max-h-[138px] rounded-lg object-cover shadow-md md:min-w-full md:max-w-[432px]`}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-center gap-4">
                            <RegularButton
                                variant={ButtonVariant.SecondaryDefault}
                                size={ButtonSize.Small}
                                label={data?.actionBtn1 || "No"}
                                onClick={() => {
                                    setAnalyticsEvent("", "InApp_Popup_Discontinue");
                                    // initBranch()
                                    //     .then(() => {
                                    //         trackEvent("InApp_Popup_Discontinue");
                                    //         setTimeout(() => {
                                    //             setrenderAnnouncement(false);
                                    //         }, 500);
                                    //     })
                                    //     .catch((error) => {
                                    //         console.error("Error initializing Branch:", error);
                                    //         setrenderAnnouncement(false);
                                    //     });
                                    setrenderAnnouncement(false);
                                }}
                                showRightIcon={false}
                                noIcon={true}
                            />
                            <RegularButton
                                variant={ButtonVariant.PrimaryDefault}
                                size={ButtonSize.Small}
                                label={data?.actionBtn2 || "Yes"}
                                onClick={() => {
                                    setAnalyticsEvent("", "InApp_Popup_Continue");
                                    cleverTapTrackEvent("InApp_Popup_Continue", {});
                                    // initBranch()
                                    //     .then(() => {
                                    //         trackEvent("InApp_Popup_Continue");
                                    //         setTimeout(() => {
                                    //             window.location.href = data?.url ?? "#"
                                    //         }, 500);
                                    //     })
                                    //     .catch((error) => {
                                    //         console.error("Error initializing Branch:", error);
                                    //         window.location.href = data?.url ?? "#"
                                    //     });
                                    window.location.href = data?.url ?? "#";
                                }}
                                showRightIcon={false}
                                noIcon={true}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
